import _fromPairs from 'lodash/fromPairs';
import { IdentityType, KeyStatus, Tone } from './types';

export const API_ENDPOINTS = {
	SEARCH_USER: '/dashboard/miscellaneous-dpdpaDataAccess-searchDpdpaUser',
	FETCH_USER_DATA: '/dashboard/miscellaneous-dpdpaDataAccess-fetchDpdpaUserData',
	EXPORT_USER_DATA: '/dashboard/miscellaneous-dpdpaDataAccess-exportDpdpaUserData'
} as const;

export const REQUEST_TIMEOUT_MS = 60000;

export const STALE_DATA_DAYS = 30;

export const LIMITS = {
	TICKET_ID_MAX: 64,
	REASON_MIN: 10,
	REASON_MAX: 500,
	IDENTIFIER_MAX: 64,
	REASON_PREVIEW: 60
} as const;

export const IDENTITY_TYPE = {
	UID: 'zupeeuserid',
	PHONE: 'mobileno',
	PAN: 'pan',
	VOTER_ID: 'voterid',
	DL: 'dl'
} as const;

// Hashed-identity search stays off until the data team confirms the hash pepper; keep in step with
// admin.service dpdpa.enabledIdentityTypes so agents aren't offered searches that will be refused
const ENABLED_IDENTITY_TYPES: IdentityType[] = [IDENTITY_TYPE.UID];

// emailid is supported by the backend but intentionally not exposed in the UI
const ALL_IDENTITY_TYPES: Array<{ id: IdentityType; label: string; placeholder: string }> = [
	{ id: IDENTITY_TYPE.UID, label: 'Zupee User ID', placeholder: 'e.g. 123456789' },
	{ id: IDENTITY_TYPE.PHONE, label: 'Phone', placeholder: 'e.g. 9876543210' },
	{ id: IDENTITY_TYPE.PAN, label: 'PAN', placeholder: 'e.g. ABCDE1234F' },
	{ id: IDENTITY_TYPE.VOTER_ID, label: 'Voter ID', placeholder: 'Voter ID number' },
	{ id: IDENTITY_TYPE.DL, label: 'Driving Licence', placeholder: 'Driving licence number' }
];

export const IDENTITY_TYPES = ALL_IDENTITY_TYPES.filter((type) => ENABLED_IDENTITY_TYPES.includes(type.id));

export const IDENTITY_TYPE_LABEL: Record<string, string> = {
	..._fromPairs(ALL_IDENTITY_TYPES.map((type) => [type.id, type.label])),
	emailid: 'Email'
};

export const KEY_STATUS = {
	ACTIVE: 'ACTIVE',
	INACTIVE: 'INACTIVE',
	ERASED_BY_USER: 'ERASED_BY_USER',
	ERASED_BY_LEGAL: 'ERASED_BY_LEGAL',
	NO_KEY: 'NO_KEY',
	KEY_CONFLICT: 'KEY_CONFLICT'
} as const;

export const KEY_STATUS_META: Record<KeyStatus, { label: string; tone: Tone }> = {
	ACTIVE: { label: 'Key active', tone: 'success' },
	INACTIVE: { label: 'Key inactive', tone: 'neutral' },
	ERASED_BY_USER: { label: 'Erased by user', tone: 'neutral' },
	ERASED_BY_LEGAL: { label: 'Erased by legal', tone: 'neutral' },
	NO_KEY: { label: 'No key', tone: 'danger' },
	KEY_CONFLICT: { label: 'Key conflict', tone: 'danger' }
};

export const ERASED_KEY_STATUSES: KeyStatus[] = [KEY_STATUS.ERASED_BY_USER, KEY_STATUS.ERASED_BY_LEGAL];

export const KEY_ISSUE_STATUSES: KeyStatus[] = [KEY_STATUS.NO_KEY, KEY_STATUS.KEY_CONFLICT];

export const STEPS = {
	IDENTIFY: 1,
	SELECT: 2,
	REVIEW: 3
} as const;

export const STEP_LABELS = [
	{ step: STEPS.IDENTIFY, label: 'Identify' },
	{ step: STEPS.SELECT, label: 'Select account' },
	{ step: STEPS.REVIEW, label: 'Review & export' }
];

export const EXPORT_FORMAT = {
	JSON: 'json',
	CSV: 'csv'
} as const;

export const CSV_COLUMNS = ['source', 'group', 'field', 'label', 'value', 'encrypted', 'redacted', 'error'];

export const DATE_FORMAT = 'DD MMM YYYY, HH:mm';
export const DATE_ONLY_FORMAT = 'DD MMM YYYY';

export const LOADING_MESSAGES = {
	SEARCH: 'Searching for accounts… this can take up to 20 seconds',
	FETCH: 'Querying data lake… this can take up to 20 seconds',
	EXPORT: 'Recording export in the audit log…'
} as const;

export const MESSAGES = {
	GENERIC_ERROR: 'Something went wrong. Please try again.',
	NO_RESPONSE: 'No response from server. The request may have timed out; please try again.',
	UNEXPECTED_RESPONSE:
		'The server returned an unexpected response (session expired, not authorized, or a gateway timeout). Please retry, and log in again if it persists.',
	EMPTY_STATE: 'No account found for this identifier.',
	EMPTY_STATE_HINT:
		'Only the user’s current identifier is searchable. If the user has changed their phone or PAN, ask the requester for the current one or search by Zupee User ID.',
	UNMAPPED_UID:
		'No encryption-key record exists for this Zupee User ID. Check the ID for typos first. If it is correct, review the data: the lake tables are still checked, and each one says whether it holds a record for this user.',
	CONFIRM_HEADER: 'Review decrypted personal data?',
	CONFIRM_AGREE: 'Show data',
	CONFIRM_CLOSE: 'Cancel',
	PARTIAL: 'Some fields could not be decrypted.',
	KEY_ISSUE: 'Encryption key unavailable — data issue, escalate to the data team.',
	COPIED: 'Copied to clipboard',
	COPY_FAILED: 'Could not copy to clipboard',
	EXPORT_FAILED: 'Could not build the export file'
} as const;

export const FIELD_ERROR_LABEL: Record<string, string> = {
	DECRYPT_FAILED: 'Decrypt failed',
	KEY_UNAVAILABLE: 'Key unavailable'
};
